import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPaymentRefund, getOperatingAccountBalance, getPaymentRecord, getTrustAccountBalance } from 'services/billingServices';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import {
  buildRefundSubmitPayload,
  computeTotalRefundAmount,
  getSplitFullRefundAmounts,
  getTrustAccountIdForRefund,
  isSplitRefundEligible,
  parseAmount,
  validateLegacyRefund,
  validateSplitRefund,
} from '../refundPaymentUtils';

const INITIAL_FORM_ERRORS = {
  amount: '',
  trust_refund_amount: '',
  earned_refund_amount: '',
  total_refund_amount: '',
  refundDate: '',
  payment_method: '',
};

/**
 * Refund payment form state, validation, and submit logic for RefundPaymentV2.
 */
export const useRefundPaymentV2 = ({ selectedRecord, isLeadSpecific, onSuccess }) => {
  const [amount, setAmount] = useState();
  const [trustRefundAmount, setTrustRefundAmount] = useState();
  const [earnedRefundAmount, setEarnedRefundAmount] = useState();
  const [fullRefund, setFullRefund] = useState(false);
  const [refundDate, setRefundDate] = useState();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState();
  const [notes, setNotes] = useState();
  const [formErrors, setFormErrors] = useState(INITIAL_FORM_ERRORS);
  const [loading, setLoading] = useState(false);
  const [recordDetails, setRecordDetails] = useState();
  const [accountBalance, setAccountBalance] = useState(0);
  const [trustBalance, setTrustBalance] = useState(0);
  const [operatingBalance, setOperatingBalance] = useState(0);

  const { addToast } = useToast();

  const isSplitRefund = useMemo(() => isSplitRefundEligible(recordDetails), [recordDetails]);

  const totalRefundAmount = useMemo(() => {
    if (!isSplitRefund) {
      return null;
    }

    return computeTotalRefundAmount(trustRefundAmount, earnedRefundAmount);
  }, [earnedRefundAmount, isSplitRefund, trustRefundAmount]);

  useEffect(() => {
    if (!selectedRecord?.case_id) {
      return;
    }

    setAmount();
    setTrustRefundAmount();
    setEarnedRefundAmount();
    setFullRefund(false);
    setFormErrors(INITIAL_FORM_ERRORS);
    setRefundDate(selectedRecord?.is_confido_transaction ? new Date() : undefined);

    getPaymentRecord(`payment_record#${selectedRecord.payment_record_id}`)
      .then((response) => {
        if (response?.data) {
          setRecordDetails({ ...response.data });
          if (response?.data?.is_confido_transaction) {
            setSelectedPaymentMethod(response?.data?.payment_method === 'ACH' ? 'other' : 'Credit Card');
          }
        }
      })
      .catch(() => {});
  }, [selectedRecord?.case_id, selectedRecord?.payment_record_id]);

  useEffect(() => {
    if (!recordDetails) {
      return;
    }

    if (isSplitRefundEligible(recordDetails)) {
      const trustAccountId = getTrustAccountIdForRefund(recordDetails);
      const operatingAccountId = recordDetails?.deposit_account_id;
      const caseId = recordDetails?.case_id;

      if (trustAccountId && caseId) {
        getTrustAccountBalance(trustAccountId, caseId)
          .then((response) => {
            setTrustBalance(parseAmount(response?.data?.available_balance) ?? 0);
          })
          .catch(() => {
            setTrustBalance(0);
          });
      }

      if (operatingAccountId && caseId) {
        getOperatingAccountBalance(operatingAccountId, caseId)
          .then((response) => {
            setOperatingBalance(parseAmount(response?.data?.available_balance) ?? 0);
          })
          .catch(() => {
            setOperatingBalance(0);
          });
      }

      return;
    }

    const accountId = recordDetails?.deposit_account_id;
    const caseId = recordDetails?.case_id;

    if (!accountId || !caseId) {
      return;
    }

    if (recordDetails?.deposit_into === 'trust') {
      getTrustAccountBalance(accountId, caseId)
        .then((response) => {
          setAccountBalance(parseAmount(response?.data?.available_balance) ?? 0);
        })
        .catch(() => {
          setAccountBalance(0);
        });
    } else if (recordDetails?.deposit_into === 'operating') {
      getOperatingAccountBalance(accountId, caseId)
        .then((response) => {
          setAccountBalance(parseAmount(response?.data?.available_balance) ?? 0);
        })
        .catch(() => {
          setAccountBalance(0);
        });
    }
  }, [recordDetails]);

  useEffect(() => {
    if (!recordDetails?.payment_record_amount || !isSplitRefund) {
      return;
    }

    const { trustRefundAmount: fullTrust, earnedRefundAmount: fullEarned } = getSplitFullRefundAmounts(recordDetails);
    const trustValue = parseAmount(trustRefundAmount) ?? 0;
    const earnedValue = parseAmount(earnedRefundAmount) ?? 0;
    const matchesFullRefund = trustValue === fullTrust && earnedValue === fullEarned && (fullTrust > 0 || fullEarned > 0);

    setFullRefund((current) => (current === matchesFullRefund ? current : matchesFullRefund));
  }, [earnedRefundAmount, isSplitRefund, recordDetails, trustRefundAmount]);

  useEffect(() => {
    if (!recordDetails?.payment_record_amount || isSplitRefund) {
      return;
    }

    const fullAmount = parseAmount(recordDetails.payment_record_amount);
    setFullRefund((current) => {
      const matchesFullRefund = amount === fullAmount;
      return current === matchesFullRefund ? current : matchesFullRefund;
    });
  }, [amount, isSplitRefund, recordDetails]);

  const clearFieldError = useCallback((field) => {
    setFormErrors((prev) => ({ ...prev, [field]: '' }));
  }, []);

  const onTrustRefundAmountChange = useCallback(
    (value) => {
      setTrustRefundAmount(value);
      clearFieldError('trust_refund_amount');
      clearFieldError('total_refund_amount');
    },
    [clearFieldError]
  );

  const onEarnedRefundAmountChange = useCallback(
    (value) => {
      setEarnedRefundAmount(value);
      clearFieldError('earned_refund_amount');
      clearFieldError('total_refund_amount');
    },
    [clearFieldError]
  );

  const onChangeRefundFull = useCallback(
    (checked) => {
      if (!checked) {
        setFullRefund(false);
        setTrustRefundAmount(undefined);
        setEarnedRefundAmount(undefined);
        clearFieldError('trust_refund_amount');
        clearFieldError('earned_refund_amount');
        clearFieldError('total_refund_amount');
        return;
      }

      if (isSplitRefundEligible(recordDetails)) {
        const { trustRefundAmount: fullTrust, earnedRefundAmount: fullEarned } = getSplitFullRefundAmounts(recordDetails);
        setTrustRefundAmount(fullTrust);
        setEarnedRefundAmount(fullEarned);
        setFullRefund(true);
        setFormErrors((prev) => ({
          ...prev,
          trust_refund_amount: '',
          earned_refund_amount: '',
          total_refund_amount: '',
        }));
        return;
      }

      setFullRefund(true);
      setAmount(parseAmount(recordDetails?.payment_record_amount) ?? 0);
      clearFieldError('amount');
    },
    [clearFieldError, recordDetails]
  );

  const validateForm = useCallback(() => {
    let validationResult = { isValid: true, errors: {} };

    if (isSplitRefund) {
      validationResult = validateSplitRefund({
        trustRefundAmount,
        earnedRefundAmount,
        recordDetails,
        trustBalance,
        operatingBalance,
      });
    } else {
      validationResult = validateLegacyRefund({
        amount,
        recordDetails,
        accountBalance,
      });
    }

    const nextErrors = {
      ...INITIAL_FORM_ERRORS,
      ...validationResult.errors,
    };

    if (!refundDate) {
      nextErrors.refundDate = 'Required';
      validationResult.isValid = false;
    }

    if (!selectedPaymentMethod) {
      nextErrors.payment_method = 'Required';
      validationResult.isValid = false;
    }

    setFormErrors(nextErrors);
    return validationResult.isValid;
  }, [
    accountBalance,
    amount,
    earnedRefundAmount,
    isSplitRefund,
    operatingBalance,
    recordDetails,
    refundDate,
    selectedPaymentMethod,
    trustBalance,
    trustRefundAmount,
  ]);

  const onSubmit = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const data = buildRefundSubmitPayload({
      recordDetails,
      notes,
      refundDate,
      selectedPaymentMethod,
      amount,
      trustRefundAmount,
      earnedRefundAmount,
      isSplitRefund,
    });

    createPaymentRefund(data, recordDetails.sk, isLeadSpecific ? isLeadSpecific : recordDetails?.is_lead)
      .then((response) => {
        setLoading(false);
        addToast(
          false,
          toastConstant.toasterType.SUCCESS,
          toastConstant.api.SUCCESS,
          response?.data?.is_confido_payment ? response?.data?.message : toastConstant.message.REFUND_REQUEST_CREATE_SUCCESS
        );
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        addToast(
          false,
          toastConstant.toasterType.ERROR,
          toastConstant.api.FAILED,
          err?.response?.data || toastConstant.message.REFUND_REQUEST_CREATE_FAILED
        );
      });
  }, [
    addToast,
    amount,
    earnedRefundAmount,
    isLeadSpecific,
    isSplitRefund,
    notes,
    onSuccess,
    recordDetails,
    refundDate,
    selectedPaymentMethod,
    trustRefundAmount,
    validateForm,
  ]);

  return {
    amount,
    setAmount,
    trustRefundAmount,
    setTrustRefundAmount,
    earnedRefundAmount,
    setEarnedRefundAmount,
    totalRefundAmount,
    fullRefund,
    onChangeRefundFull,
    refundDate,
    setRefundDate,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    notes,
    setNotes,
    formErrors,
    clearFieldError,
    loading,
    recordDetails,
    isSplitRefund,
    onSubmit,
    onTrustRefundAmountChange,
    onEarnedRefundAmountChange,
  };
};
